import { formatDistanceToNow } from "date-fns";

import type { BadgeVariant } from "@/components/ui/badge";
import type { NotificationCategory } from "@/types/notification";

/** Human label for a category chip. */
export const CATEGORY_LABEL = {
  GENERAL: "General",
  LEAVE: "Leave",
  TASK: "Task",
  EXPENSE: "Expense",
  ATTENDANCE: "Attendance",
  INVENTORY: "Inventory",
} satisfies Record<NotificationCategory, string>;

/** Badge variant per category (falls back to muted). */
export const CATEGORY_VARIANT = {
  GENERAL: "muted",
  LEAVE: "primary",
  TASK: "success",
  EXPENSE: "warning",
  ATTENDANCE: "outline",
  INVENTORY: "default",
} satisfies Record<NotificationCategory, BadgeVariant>;

/** All categories, for the filter dropdown (keep in sync with `NotificationCategory`). */
export const NOTIFICATION_CATEGORIES = [
  "GENERAL",
  "LEAVE",
  "TASK",
  "EXPENSE",
  "ATTENDANCE",
  "INVENTORY",
] as const satisfies readonly NotificationCategory[];

/**
 * Underline-style tab classes (bottom border indicator, not the pill/box
 * default) — mirrors the profile-management `SidebarNav` tabs so the
 * All/Unread switch reads as the same pattern across the app.
 */
export const UNDERLINE_TABS_LIST_CLASS =
  "p-0 bg-background justify-start border-none rounded-none h-7 w-fit";
export const UNDERLINE_TABS_TRIGGER_CLASS =
  "rounded-none bg-background h-full data-[state=active]:shadow-none border-b-2 border-transparent border-x-0 border-t-0 data-[state=active]:border-primary px-3 whitespace-nowrap py-0";

/** Abbreviates date-fns unit words ("minutes" -> "min", "seconds" -> "sec", etc). */
function abbreviateUnits(text: string): string {
  return text
    .replace(/\bseconds?\b/, "sec")
    .replace(/\bminutes?\b/, "min")
    .replace(/\bhours?\b/, "hr")
    .replace(/\bmonths?\b/, "mo");
}

/** "3 hrs ago"-style relative time with abbreviated units; empty on parse failure. */
export function relativeTime(iso: string): string {
  try {
    return abbreviateUnits(
      formatDistanceToNow(new Date(iso), { addSuffix: true })
    );
  } catch {
    return "";
  }
}
